import { toCanonicalStatus } from "./orgDesign.constants";

const STATUSES = ["UNCHANGED", "AI AUGMENTED", "REDUCED", "REDUNDANT", "CREATED"];
const DEFAULT_AVG_COST_FOR_SAVINGS = 80000;

// Keyed on the entry's own generated id, not its title — two proposed roles
// can share a title, which would otherwise collide them into one node.
const NEW_ROLE_ID_PREFIX = "new_";
export const newRoleNodeId = (id) => `${NEW_ROLE_ID_PREFIX}${id}`;

// reports_to only accepts a real JD's sb_unique_id — a proposed role's
// newRoleNodeId (or a stale one from an older draft) never exists server-side.
export const isProposedRoleId = (id) => typeof id === "string" && id.startsWith(NEW_ROLE_ID_PREFIX);

// Roles/proposed roles whose manager is a proposed role — blocked on save.
export const findRolesReportingToProposedRoles = (roles, newRoles) =>
  [...roles, ...(newRoles || []).map(toPseudoRole)].filter((r) => isProposedRoleId(r.manager_id));

// Floors to a whole, non-negative headcount. Unlike the backend's original
// safe_after_hc, this allows raising a headcount too, not just trimming —
// Table View editing needs both directions.
export const safeAfterHc = (beforeHc, afterHcRaw) => {
  const before = Math.trunc(Number(beforeHc) || 0);
  const afterNum = Number(afterHcRaw);
  if (!Number.isFinite(afterNum)) return before;
  return Math.max(0, Math.floor(afterNum));
};

// Some backend rows send Python's `None` as the literal string "None"
// rather than JSON null — treated as missing here too. Anything else is
// mapped onto the canonical status vocabulary (e.g. "INCREASED" ->
// "AI AUGMENTED"), so the table, tree, legend, counts and save payload all
// agree on one spelling.
const NONE_STATUS_VALUES = new Set(["none", "null", ""]);
const normalizeStatusValue = (status) =>
  !status || NONE_STATUS_VALUES.has(String(status).trim().toLowerCase()) ? "UNCHANGED" : toCanonicalStatus(status);

// Deliberately does NOT downgrade AI AUGMENTED/REDUCED back to UNCHANGED just
// because after_hc equals total_positions — the AI can legitimately report
// AI AUGMENTED for a capacity/productivity change with no headcount change at
// all. "User reverted their own edit back to the original number" is a
// different case, handled only in updateHeadcount (useOrgDesignData.js),
// which knows it's looking at a live edit; this function doesn't.
export const normalizeDesign = (roles, design) => {
  const byId = Object.fromEntries(roles.map((r) => [r.sb_unique_id, r]));
  const normalized = {};
  Object.entries(design || {}).forEach(([rid, v]) => {
    const role = byId[rid];
    if (!role) { normalized[rid] = { ...v, status: normalizeStatusValue(v.status) }; return; }
    const corrected = safeAfterHc(role.total_positions, v.after_hc ?? role.total_positions);
    let status = normalizeStatusValue(v.status);
    if (corrected === 0) status = "REDUNDANT";
    normalized[rid] = { ...v, after_hc: corrected, status };
  });
  return normalized;
};

// `placedIds` is shared (not cloned) across the whole recursion, so each
// role can only ever be placed once in the tree even if the underlying
// manager_id chain contains a cycle (A reports to B, B reports to A) — a
// cloned-per-branch visited set would still let a cyclic role render twice
// (once at its real spot, once truncated under its own cycle-mate).
//
// `childrenByManagerId` is grouped once by the caller (see the builders below)
// rather than this function re-filtering the full roles array at every
// node — for a hierarchy of n roles that re-filter runs n times, each O(n),
// so building the whole tree was O(n²). Grouping up front makes each node's
// own children an O(1) map lookup instead, so the whole tree builds in O(n).
const buildTree = (childrenByManagerId, design, nodeId, state, placedIds) => {
  const candidates = childrenByManagerId.get(nodeId) || [];
  const children = candidates.filter((r) => !placedIds.has(r.sb_unique_id));
  return children.map((row) => {
    const rid = row.sb_unique_id;
    placedIds.add(rid);
    const hcBefore = Number(row.total_positions) || 0;
    const res = design[rid] || {};
    const hcAfter = res.after_hc != null ? Number(res.after_hc) : hcBefore;
    const statusLower = (res.status || "UNCHANGED").toLowerCase();

    const [hc, statusLabel] = state === "before" ? [hcBefore, "unchanged"] : [hcAfter, statusLower];
    const node = { id: rid, l: row.job_title, n: hc, s: statusLabel };
    node.ch = buildTree(childrenByManagerId, design, rid, state, placedIds);
    return node;
  });
};

const subtreeHc = (nodes) => nodes.reduce((sum, n) => sum + n.n + subtreeHc(n.ch), 0);

// Lets a `newRoles` entry flow through the same buildTree recursion as a
// real role, with no special-casing.
const toPseudoRole = (nr) => ({
  sb_unique_id: newRoleNodeId(nr.id),
  manager_id: nr.manager_id,
  manager_is_external: nr.manager_is_external,
  job_title: nr.role_title,
  total_positions: 0,
  microtask_function: nr.microtask_function,
});

// The synthetic parent id every real root hangs off. Never rendered — it only
// gives buildTree a single key to start its recursion from.
const TOP = "__ORG_ROOT__";

// Folds `newRoles` into the role set as pseudo-roles for the "after" state so
// buildTree never has to special-case them. Shared by both builders.
const prepareRoles = (roles, design, newRoles, state) => {
  const pseudoRoles = state === "after" ? (newRoles || []).map(toPseudoRole) : [];
  const pseudoDesign = {};
  if (state === "after") {
    (newRoles || []).forEach((nr) => {
      pseudoDesign[newRoleNodeId(nr.id)] = { after_hc: Number(nr.after_hc ?? 1), status: "CREATED" };
    });
  }
  const allRoles = [...roles, ...pseudoRoles];
  return {
    allRoles,
    allDesign: { ...(design || {}), ...pseudoDesign },
    validIds: new Set(allRoles.map((r) => r.sb_unique_id)),
  };
};

// Groups roles under whichever node should parent them. `keyFor` returning
// null drops the role from the tree entirely. Grouped once, up front — see
// buildTree's comment for why.
const groupByParent = (allRoles, keyFor) => {
  const childrenByManagerId = new Map();
  allRoles.forEach((r) => {
    const key = keyFor(r);
    if (key == null) return;
    if (!childrenByManagerId.has(key)) childrenByManagerId.set(key, []);
    childrenByManagerId.get(key).push(r);
  });
  return childrenByManagerId;
};

// Current Org only — real hierarchy roots as returned by GET
// /organization-view's roots[] (resolved job_id-safe, same as every other
// manager reference here — see mapCurrentOrgStructureRole), so the CEO (or
// whichever role(s) organization-view names as top-of-org) is the actual top
// node. PAIC has no equivalent nested-hierarchy endpoint, so it roots on
// buildOrgForestFromOrphanRoots instead.
//
// Built by grouping on manager_id rather than by walking organization-view's
// own nested children[] directly — the two produce the same edges
// (organization-view's nesting is itself derived from these same manager
// pointers), and grouping on manager_id is what lets a live "Reports To" edit
// keep moving the node in this tree, instead of the chart going stale the
// moment the user reparents someone.
//
// A role with no valid manager that ISN'T one of `rootIds` (a broken/missing
// reporting line — GET /organization-view's own `warnings[]` is where that
// gets surfaced, see useOrgDesignData.js) is deliberately left out of the
// tree rather than rooted — buildOrgForestFromOrphanRoots is the builder that
// roots those instead.
export const buildOrgForestFromRealRoots = (roles, design, newRoles, state, rootIds) => {
  const { allRoles, allDesign, validIds } = prepareRoles(roles, design, newRoles, state);
  const rootIdSet = new Set(rootIds);

  const childrenByManagerId = groupByParent(allRoles, (r) => {
    if (rootIdSet.has(r.sb_unique_id)) return TOP;
    const raw = r.manager_id;
    if (raw && validIds.has(raw)) return raw;
    // A manager deliberately picked from outside this design (Role Reporting)
    // isn't a broken line — root its branch rather than hiding it.
    return r.manager_is_external ? TOP : null; // orphan — see function comment
  });

  const placedIds = new Set();
  const rootNodes = buildTree(childrenByManagerId, allDesign, TOP, state, placedIds);
  return { id: "root", l: "Org", n: subtreeHc(rootNodes), s: "unchanged", ch: rootNodes };
};

// The default tree, used whenever no apex role is named: every PAIC caller
// (a PAIC selection has no /organization-view to ask), and a Current Org
// whose view returned roots[] = [] (nothing flagged is_apex_role) or named
// ids this role set doesn't contain.
//
// Roots on the roles that HAVE no manager rather than on their function, so a
// real role is always the top node. Rooting on the function is what the
// retired buildOrgForest did, and it is the bug this replaced twice over:
// every role in a PAIC selection shares one `function`, the apex has no
// manager inside the selection, so the entire group rendered under a single
// synthetic box labelled "CSV - Customer Service" — a function, not a role,
// and never meant to be shown.
//
// Unlike buildOrgForestFromRealRoots, a role whose manager is missing from the
// role set is rooted here rather than dropped: with no apex to anchor the
// tree, dropping every orphan would render nothing at all.
export const buildOrgForestFromOrphanRoots = (roles, design, newRoles, state) => {
  const { allRoles, allDesign, validIds } = prepareRoles(roles, design, newRoles, state);

  const childrenByManagerId = groupByParent(allRoles, (r) => {
    const raw = r.manager_id;
    return raw && validIds.has(raw) ? raw : TOP;
  });

  const placedIds = new Set();
  const rootNodes = buildTree(childrenByManagerId, allDesign, TOP, state, placedIds);
  return { id: "root", l: "Org", n: subtreeHc(rootNodes), s: "unchanged", ch: rootNodes };
};

export const computeStats = (roles, design, newRoles, avgCostForSavings = DEFAULT_AVG_COST_FOR_SAVINGS) => {
  const asIsHc = roles.reduce((sum, r) => sum + (Number(r.total_positions) || 0), 0);
  const totalVal = roles.reduce((sum, r) => sum + (Number(r.potential_value_usd) || 0), 0);

  const counts = Object.fromEntries(STATUSES.map((s) => [s, 0]));
  counts.CREATED = (newRoles || []).length;

  let hcAfterExisting = 0;
  let hcReducedDelta = 0;
  let hcRedundantDelta = 0;
  // Per-role headcount delta, independent of the status label — a role can
  // be re-labeled AI AUGMENTED while its headcount is actually reduced, so
  // bucketing by status alone would misclassify it. Feeds only the Summary
  // Bridge's Reductions/Additions tiles, not hc_reduction/hc_created below.
  let existingHcIncrease = 0;
  let existingHcDecrease = 0;
  let existingHcIncreasedRoles = 0;
  let existingHcDecreasedRoles = 0;

  // Built once instead of `roles.find(...)` per design entry — for n roles
  // each with a design entry, that find() made this whole loop O(n²).
  const rolesById = new Map(roles.map((r) => [r.sb_unique_id, r]));

  Object.entries(design || {}).forEach(([rid, v]) => {
    const status = v.status || "UNCHANGED";
    counts[status] = (counts[status] || 0) + 1;
    const row = rolesById.get(rid);
    const before = row ? Number(row.total_positions) || 0 : 0;
    const after = v.after_hc != null ? Number(v.after_hc) : before;
    hcAfterExisting += after;
    if (status === "REDUCED") hcReducedDelta += before - after;
    if (status === "REDUNDANT") hcRedundantDelta += before;
    const delta = after - before;
    if (delta > 0) {
      existingHcIncrease += delta;
      existingHcIncreasedRoles += 1;
    } else if (delta < 0) {
      existingHcDecrease += -delta;
      existingHcDecreasedRoles += 1;
    }
  });

  // Roles with no design entry at all (never touched by an edit) still count
  // as UNCHANGED at their original headcount.
  const untouched = roles.filter((r) => !(design || {})[r.sb_unique_id]);
  untouched.forEach((r) => {
    counts.UNCHANGED = (counts.UNCHANGED || 0) + 1;
    hcAfterExisting += Number(r.total_positions) || 0;
  });

  const hcCreated = (newRoles || []).reduce((sum, r) => sum + Number(r.after_hc ?? 1), 0);
  const totalAfterHc = hcAfterExisting + hcCreated;
  const hcReduction = asIsHc - hcAfterExisting;
  const reductionPct = asIsHc > 0 ? (hcReduction / asIsHc) * 100 : 0;
  const netHcReduction = asIsHc - totalAfterHc;
  const netReductionPct = asIsHc > 0 ? (netHcReduction / asIsHc) * 100 : 0;
  const annualNewCost = (newRoles || []).reduce(
    (sum, r) => sum + Number(r.after_hc ?? 1) * Number(r.annual_cost_usd_per_fte ?? 140000),
    0
  );
  const savingsReduced = hcReducedDelta * avgCostForSavings;
  const savingsRedundant = hcRedundantDelta * avgCostForSavings;

  return {
    stats: {
      as_is_hc: asIsHc,
      total_after_hc: totalAfterHc,
      hc_reduction: hcReduction,
      reduction_pct: reductionPct,
      net_hc_reduction: netHcReduction,
      net_reduction_pct: netReductionPct,
      total_val: totalVal,
      hc_created: hcCreated,
      annual_new_cost: annualNewCost,
      savings_reduced: savingsReduced,
      savings_redundant: savingsRedundant,
      existing_hc_increase: existingHcIncrease,
      existing_hc_decrease: existingHcDecrease,
      existing_hc_increased_roles: existingHcIncreasedRoles,
      existing_hc_decreased_roles: existingHcDecreasedRoles,
    },
    status_counts: counts,
  };
};

// Budget is an Org-Design-only addition, not part of org_redesign_agent.py's
// output. Used both for live recompute on every edit and, once with an
// empty design/no new roles, to freeze the baseline at workspace creation.
export const computeBudget = (roles, design, newRoles) => {
  const existingBudget = roles.reduce((sum, r) => {
    const override = (design || {})[r.sb_unique_id];
    const afterHc = override?.after_hc != null ? Number(override.after_hc) : Number(r.total_positions) || 0;
    return sum + afterHc * (Number(r.annual_compensation) || 0);
  }, 0);
  const newRolesBudget = (newRoles || []).reduce(
    (sum, r) => sum + Number(r.after_hc ?? 1) * Number(r.annual_cost_usd_per_fte ?? 140000),
    0
  );
  return existingBudget + newRolesBudget;
};

// Deliberately excludes baseline_budget/targetBudget — those are frozen or
// user-set once per workspace, not recomputed on every edit; recompute()
// (useOrgDesignData.js) preserves them from the previous draft.
//
// NO tree ever roots on a function, on any path. `rootIds` (Current Org
// only, from /organization-view — see buildOrgForestFromRealRoots) chooses
// only HOW the top of the chart is found: when it names apex roles this role
// set actually contains, those are the roots, and a broken reporting line
// drops its branch rather than being papered over. Everything else roots
// every manager-less role on itself:
//   - a PAIC caller, which has no /organization-view to ask (`undefined`);
//   - organization-view returned roots[] = [] because nothing is flagged
//     is_apex_role;
//   - it named roots that this role set doesn't contain (e.g. reopening a
//     saved version whose own current_org_structure.roles snapshot predates
//     today's CEO, while rootIds resolved against the live org — see
//     fetchOrgRedesignVersionDraft).
export const buildOrgDesignResult = (roles, design, newRoles, rationale = "", avgCostForSavings, rootIds) => {
  const normalizedDesign = normalizeDesign(roles, design);
  const { stats, status_counts } = computeStats(roles, normalizedDesign, newRoles, avgCostForSavings);
  const forest = (state, d, nr) => {
    if (rootIds?.length) {
      const real = buildOrgForestFromRealRoots(roles, d, nr, state, rootIds);
      if (real.ch.length > 0) return real;
    }
    return buildOrgForestFromOrphanRoots(roles, d, nr, state);
  };
  return {
    before_tree: forest("before", {}, []),
    after_tree: forest("after", normalizedDesign, newRoles),
    design: normalizedDesign,
    new_roles: newRoles || [],
    org_design_rationale: rationale,
    stats,
    status_counts,
    projected_budget: computeBudget(roles, normalizedDesign, newRoles),
  };
};

// Every role reporting (transitively) to rootId, including rootId itself —
// excluded from reparent options so a role can never be moved under its own
// descendant.
export const getDescendantIds = (roles, rootId) => {
  const ids = new Set();
  const visit = (id) => {
    if (ids.has(id)) return;
    ids.add(id);
    roles.filter((r) => r.manager_id === id).forEach((child) => visit(child.sb_unique_id));
  };
  visit(rootId);
  return ids;
};

// A role is unreachable if repeatedly following manager_id never terminates
// at a real root (no manager, or a manager outside this role set) — the
// chain cycles back on itself instead. Such roles never get placed by
// buildTree (nothing above them ever leads back to a synthetic ROOT node),
// so they'd silently vanish from the tree while still counting toward
// budget/stats totals computed from the flat roles list. Returns the set of
// role ids caught in a cycle, regardless of whether it came from a user's
// own edit (already blocked by reparentOptionsFor excluding descendants)
// or was already present in imported data.
export const findUnreachableRoleIds = (roles, newRoles) => {
  const allRoles = [...roles, ...(newRoles || []).map(toPseudoRole)];
  const byId = new Map(allRoles.map((r) => [r.sb_unique_id, r]));
  const unreachable = new Set();
  allRoles.forEach((role) => {
    const chainIds = [];
    const chainSet = new Set();
    let current = role;
    while (current && !chainSet.has(current.sb_unique_id)) {
      chainSet.add(current.sb_unique_id);
      chainIds.push(current.sb_unique_id);
      current = current.manager_id ? byId.get(current.manager_id) : null;
    }
    if (current) chainIds.forEach((id) => unreachable.add(id)); // stopped on a repeat, not a real root
  });
  return unreachable;
};

// Tallies statuses inside a node's hidden subtree so collapsing it doesn't
// hide the fact that changes exist inside — UNCHANGED is skipped, since a
// subtree that's entirely unchanged has nothing worth calling out.
export const summarizeSubtreeStatuses = (node) => {
  const byStatus = {};
  let total = 0;
  const walk = (n) => {
    (n.ch || []).forEach((child) => {
      total += 1;
      const status = (child.s || "unchanged").toUpperCase();
      if (status !== "UNCHANGED") byStatus[status] = (byStatus[status] || 0) + 1;
      walk(child);
    });
  };
  walk(node);
  return { total, byStatus, hasChanges: Object.keys(byStatus).length > 0 };
};

// Bucketed by each role's actual Target-Positions delta, not its status
// label — a role can carry AI AUGMENTED while its headcount is reduced, so
// this stays independent of updateStatus/updateHeadcount's status tagging.
// Shared by OrgDesignSummaryBridge and the PDF export's Summary page
// (utils/orgDesignPdf.jsx) so the two can never disagree on a figure.
export const getHeadcountBridge = (stats, statusCounts) => {
  const createdRoles = statusCounts?.CREATED || 0;
  return {
    currentFtes: stats.as_is_hc,
    reductions: stats.existing_hc_decrease || 0,
    additions: (stats.existing_hc_increase || 0) + (stats.hc_created || 0),
    decreasedRoles: stats.existing_hc_decreased_roles || 0,
    addedRoles: (stats.existing_hc_increased_roles || 0) + createdRoles,
    netPositions: stats.total_after_hc,
  };
};
